import React, { useMemo } from 'react';
import { Box, Text, VStack } from '@chakra-ui/react';
import {
  buildSvgPolygonPoints,
  celsiusToFahrenheit,
  generateOperationEnvelopeData,
  CompressorEnvelopeFamily,
} from '../../utils/operationEnvelope';
import { useI18n } from '~/i18n';
import { CHART_FONT_FAMILY } from '~/styles/chart';

type Props = {
  refrigerante: string;
  tempUnit: 'c' | 'f';
  evapTemp?: number;
  condTemp?: number;
  family: CompressorEnvelopeFamily;
  application?: string;
};

const isValidNumber = (value: any) => typeof value === 'number' && Number.isFinite(value);

export function OperationEnvelopeChart({
  refrigerante,
  tempUnit,
  evapTemp,
  condTemp,
  family,
  application,
}: Props) {
  const { t, language } = useI18n();
  const chartData = useMemo(
    () =>
      generateOperationEnvelopeData({
        refrigerante,
        tempUnit,
        evapTemp,
        condTemp,
        language,
        family,
        application,
      }),
    [refrigerante, tempUnit, evapTemp, condTemp, language, family, application]
  );

  const view = useMemo(() => {
    const { envelope } = chartData;
    const useFahrenheit = tempUnit === 'f';

    const convertTemp = (value: number) => {
      if (useFahrenheit) {
        return celsiusToFahrenheit(value);
      }

      return value;
    };

    const chartWidth = 980;
    const chartHeight = 780;
    const margin = { top: 52, right: 16, bottom: 58, left: 64 };

    const polygonXValues = envelope.displayPolygons.flatMap((polygon) => polygon.x);
    const visualXLimits = polygonXValues.length
      ? [Math.min(...polygonXValues) - 5, Math.max(...polygonXValues) + 5]
      : envelope.xlim;

    const xMin = convertTemp(visualXLimits[0]);
    const xMax = convertTemp(visualXLimits[1]);
    const yMin = convertTemp(envelope.ylim[0]);
    const yMax = convertTemp(envelope.ylim[1]);

    const scaleX = (value: number) =>
      margin.left +
      ((convertTemp(value) - xMin) / (xMax - xMin || 1)) * (chartWidth - margin.left - margin.right);

    const scaleY = (value: number) =>
      chartHeight -
      margin.bottom -
      ((convertTemp(value) - yMin) / (yMax - yMin || 1)) * (chartHeight - margin.top - margin.bottom);

    const xTicks = envelope.xticks.map((tick) => ({
      original: tick,
      value: convertTemp(tick),
      x: scaleX(tick),
    }));

    const yTicks = envelope.yticks.map((tick) => ({
      original: tick,
      value: convertTemp(tick),
      y: scaleY(tick),
    }));

    const operatingPoint =
      isValidNumber(evapTemp) && isValidNumber(condTemp)
        ? {
            x: scaleX(evapTemp),
            y: scaleY(condTemp),
          }
        : null;

    return {
      chartWidth,
      chartHeight,
      margin,
      scaleX,
      scaleY,
      xTicks,
      yTicks,
      operatingPoint,
    };
  }, [chartData, tempUnit, evapTemp, condTemp]);

  return (
    <VStack
      align="stretch"
      spacing={3}
      width="100%"
      height="100%"
      fontFamily={CHART_FONT_FAMILY}
    >
      <Box flex="1" minH="360px" width="100%">
        <svg
          viewBox={`0 0 ${view.chartWidth} ${view.chartHeight}`}
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="Envelope de operação"
          style={{ fontFamily: CHART_FONT_FAMILY }}
        >
          <rect
            x={0}
            y={0}
            width={view.chartWidth}
            height={view.chartHeight}
            fill="#ffffff"
          />

          {view.yTicks.map((tick) => (
            <g key={`y-${tick.original}`}>
              <line
                x1={view.margin.left}
                y1={tick.y}
                x2={view.chartWidth - view.margin.right}
                y2={tick.y}
                stroke="#d9d9d9"
                strokeDasharray="4 4"
              />
              <text
                x={view.margin.left - 8}
                y={tick.y + 4}
                textAnchor="end"
                fontSize="14"
                fill="#333333"
              >
                {Math.round(tick.value)}
              </text>
            </g>
          ))}

          {view.xTicks.map((tick) => (
            <g key={`x-${tick.original}`}>
              <line
                x1={tick.x}
                y1={view.margin.top}
                x2={tick.x}
                y2={view.chartHeight - view.margin.bottom}
                stroke="#d9d9d9"
                strokeDasharray="4 4"
              />
              <text
                x={tick.x}
                y={view.chartHeight - view.margin.bottom + 18}
                textAnchor="middle"
                fontSize="14"
                fill="#333333"
              >
                {Math.round(tick.value)}
              </text>
            </g>
          ))}

          {chartData.envelope.displayPolygons.map((polygon, index) => (
            <polygon
              key={`polygon-${String(index)}`}
              points={buildSvgPolygonPoints(polygon, view.scaleX, view.scaleY)}
              fill={polygon.color || '#808080'}
              stroke="#ffffff"
              strokeWidth="1.8"
              strokeDasharray={polygon.lineDash === 'dashed' ? '8 6' : undefined}
              opacity="0.95"
            />
          ))}

          <g transform={`translate(${view.margin.left + 10} 12)`}>
            <rect
              x="0"
              y="0"
              width="190"
              height="30"
              rx="3"
              fill="#ffffff"
              stroke="#808080"
              strokeWidth="1"
            />
            <circle cx="17" cy="15" r="7" fill="red" />
            <text x="33" y="20" fontSize="16" fill="#111111">
              {t('Ponto de Operação')}
            </text>
          </g>

          <rect
            x={view.margin.left}
            y={view.margin.top}
            width={view.chartWidth - view.margin.left - view.margin.right}
            height={view.chartHeight - view.margin.top - view.margin.bottom}
            fill="none"
            stroke="#000000"
            strokeWidth="1.6"
          />

          {view.operatingPoint ? (
            <>
              <circle
                cx={view.operatingPoint.x}
                cy={view.operatingPoint.y}
                r="7"
                fill="red"
                stroke="#ffffff"
                strokeWidth="2"
              />
            </>
          ) : null}

          <text
            x={view.chartWidth / 2}
            y={view.chartHeight - 8}
            textAnchor="middle"
            fontSize="20"
            fontWeight="700"
            fill="#111111"
          >
            {chartData.xLabel}
          </text>

          <text
            x={18}
            y={view.chartHeight / 2}
            textAnchor="middle"
            fontSize="20"
            fontWeight="700"
            fill="#111111"
            transform={`rotate(-90 18 ${view.chartHeight / 2})`}
          >
            {chartData.yLabel}
          </text>
        </svg>
      </Box>

      <Text textAlign="center" fontSize="20px" fontWeight="700" color={chartData.statusColor}>
        {chartData.note || t('Informe evaporação, condensação e refrigerante para visualizar o envelope.')}
      </Text>
    </VStack>
  );
}

export default OperationEnvelopeChart;
